import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm, useWatch} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {useCreateSummary, useQuerySummary, useUpdateSummary} from "@/service/summary.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue} from "@/helper/method";
import {isNumber} from "lodash";
import {useAuthInfo} from "@/store/account.c";
import {roleAlias} from "@/helper/constant";

const validationSchema = z.object({
    id: z.number().nullable().default(0),
    title: z.string({message: "请填写标题"}),
    categoryId: z.number().nullable(),
    callVolume: z.number({message: "请填写通话量"}).or(z.literal('')).default(0).optional(),
    roleAlias: z.unknown(),
    category: z.string({message: "请选择总结类型"}),
    content: z.string({message: "请填写工作内容"}).nonempty({message: "请填写工作内容"}),
    planning: z.unknown(),
}).superRefine((values, ctx) => {
    if (values.categoryId == 1 && values.roleAlias == roleAlias.marketing) {
        if (!isNumber(values.callVolume)) {
            ctx.addIssue(
                {code: z.ZodIssueCode.custom, message: "请填写通话量", path: ['callVolume']}
            )
        }
        if (parseInt(values!.callVolume!.toString()) >= 1000) {
            ctx.addIssue(
                {code: z.ZodIssueCode.custom, message: "填写的通话量不能超过1000", path: ['callVolume']}
            )
        }
    }
})

export type WriteSummaryForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteSummary = () => {
    const allDict = useAllDict()
    const {summaryType} = allDict
    const {write, id: drawerId} = useDrawerModal()
    const {setToast} = useGlobalActions()
    const {data: summaryData} = useQuerySummary({id: drawerId!}, isNumber(drawerId))
    const {mutateAsync: createSummaryMutate, isPending: createSummaryPending} = useCreateSummary()
    const {mutateAsync: updateSummaryMutate, isPending: updateSummaryPending} = useUpdateSummary()
    const summary = summaryData?.data
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()
    const {role} = useAuthInfo()
    const authInfo = useAuthInfo()

    const initValues = () => {
        if (write == 'edit') {
            return {...summary, roleAlias: role?.roleAlias}
        }
        return {}
    }

    const {
        register,
        control,
        setValue,
        reset,
        handleSubmit,
        formState: {errors},
    } = useForm<WriteSummaryForm>({
        resolver: zodResolver(validationSchema),
        values: initValues() as WriteSummaryForm,
        defaultValues: {...defaultValues, roleAlias: role?.roleAlias, title: `${authInfo?.username}的工作总结`, category: '日总结', categoryId: 1},
    });

    const onSubmit = async (values: WriteSummaryForm) => {
        const result = write == 'add' ? await createSummaryMutate(values) : await updateSummaryMutate({...values, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    const CallVolumeWatched = () => {
        const categoryId = useWatch({
            control,
            name: "categoryId",
        })
        if (categoryId == 1 && role?.roleAlias == roleAlias.marketing) {
            return <Grid size={12}> <FormControl fullWidth={true} autoFocus={true} required={true}>
                <FormLabel>通话量</FormLabel>
                <Controller control={control} render={({field: {onChange, value}}) => <TextField
                    error={!!errors?.callVolume}
                    helperText={errors?.callVolume?.message}
                    value={value}
                    onChange={event => onChange(parseInt(event.target.value) || null)}
                    placeholder="填写通话量"
                    type="number"
                    variant="outlined"
                />} name={"callVolume"}/>
            </FormControl></Grid>
        }
    }

    const PlanWatched = () => {
        const category = useWatch({
            control,
            name: "category",
        })

        const label = category == '月总结' ? '下' : '明' + category?.substring(0, 1) + '计划'
        return <FormControl fullWidth={true}>
            <FormLabel>{label}</FormLabel>
            <TextField
                multiline
                rows={3}
                {...register("planning")}
                slotProps={{
                    input: {
                        sx: {height: 80},
                        inputProps: {maxLength: 250},
                    },
                }}
                placeholder={`输入${label}`}
                fullWidth
            />
        </FormControl>
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>标题</FormLabel>
                        <TextField
                            error={!!errors?.title}
                            helperText={errors?.title?.message}
                            {...register("title")}
                            placeholder="填写标题"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>总结类型</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={summaryType}
                                value={value as any}
                                onChange={(_, option: DictPO | null) => {
                                    setValue("categoryId", option!.value)
                                    onChange(option?.label)
                                }}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.category}
                                               helperText={errors?.category?.message}
                                               variant="outlined"
                                               placeholder={"选择总结类型"} {...params} />
                                )}
                            />
                        }} name={"category"}/>
                    </FormControl>
                </Grid>
                <CallVolumeWatched/>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>工作内容</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            error={!!errors?.content}
                            helperText={errors?.content?.message}
                            {...register("content")}
                            slotProps={{
                                input: {
                                    sx: {height: 80},
                                    inputProps: {maxLength: 250},
                                },
                            }}
                            placeholder="输入工作内容"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <PlanWatched/>
                </Grid>
            </Grid>
            <DrawerFooter loading={createSummaryPending || updateSummaryPending} onReset={() => {
                reset(defaultValues)
            }}/>
        </Box>
    );
};
